/**
 * PrecheckPlaygroundPage  (route: /precheck-playground)
 *
 * The headless page an audio-only egress loads to record a pre-check
 * playground voice session (lms-backend precheck/recording.ts). It joins the
 * room as a hidden, muted recorder and renders the room's audio and nothing
 * else: there is no deck or transcript to capture.
 *
 * Unlike the lesson recorder (/ai-tutor), it ENDS ITS OWN RECORDING, because
 * a web egress outlives the room it records: on `precheck_result` or
 * `lesson_ended` (after a short tail), when the room goes away, if it never
 * gets in, or at a hard cap. Ending is two things, both best-effort:
 *   - POST /api/v2/ai-tutor/precheck/recording/stop with its recorder token
 *     (`keepalive`, so it is sent even as the page is torn down): lms stops
 *     the egress and marks the run's recording stopped;
 *   - `END_RECORDING` on the console, the egress's own end signal.
 *
 * URL params (built by lms-backend): livekitToken, wsUrl.
 */
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LiveKitRoom, RoomAudioRenderer, useRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import { datadogLogs } from '@datadog/browser-logs';

import { BACKEND_URL } from 'configs/config';
import { HARD_CAP_MS, JOIN_GIVE_UP_MS, TAIL_MS, endsRecording } from './endSignal';

const log = (message: string, extra: Record<string, unknown> = {}) =>
  datadogLogs.logger.info(message, { label: 'precheck-playground-recorder', source: 'recording-app', ...extra });

const EndOnSessionEnd = ({ token }: { token: string }) => {
  const room = useRoomContext();
  const ended = useRef(false);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const end = (why: string, tailMs = 0) => {
      if (ended.current) return;
      ended.current = true;
      timers.push(
        setTimeout(() => {
          log('ending the recording', { why });
          fetch(`${BACKEND_URL}/api/v2/ai-tutor/precheck/recording/stop`, {
            method: 'POST',
            keepalive: true,
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => {});
          // The egress's own signal: the recording closes and uploads even if lms never hears.
          console.log('END_RECORDING');
        }, tailMs),
      );
    };
    const onData = (payload: Uint8Array) => {
      if (endsRecording(payload)) end('the session ended', TAIL_MS);
    };
    const onDisconnected = () => end('the room went away');
    const onConnected = () => log('recorder joined', { room: room.name });
    room.on(RoomEvent.DataReceived, onData);
    room.on(RoomEvent.Disconnected, onDisconnected);
    room.on(RoomEvent.Connected, onConnected);
    timers.push(setTimeout(() => { if (room.state !== 'connected') end('it never got into the room'); }, JOIN_GIVE_UP_MS));
    timers.push(setTimeout(() => end('the hard cap'), HARD_CAP_MS));
    return () => {
      room.off(RoomEvent.DataReceived, onData);
      room.off(RoomEvent.Disconnected, onDisconnected);
      room.off(RoomEvent.Connected, onConnected);
      timers.forEach(clearTimeout);
    };
  }, [room, token]);

  return null;
};

const PrecheckPlaygroundPage = () => {
  const [searchParams] = useSearchParams();
  const livekitToken = searchParams.get('livekitToken');
  const wsUrl = searchParams.get('wsUrl');

  if (!livekitToken || !wsUrl) {
    // Nothing to record: end at once rather than encode an error page.
    console.log('END_RECORDING');
    return null;
  }

  return (
    <LiveKitRoom
      serverUrl={wsUrl}
      token={livekitToken}
      connect={true}
      audio={false}
      video={false}
      onError={(error) => {
        datadogLogs.logger.error(
          'Error in the pre-check playground recorder room',
          { label: 'livekit-error', source: 'recording-app', error: error.message },
          error,
        );
      }}
    >
      <RoomAudioRenderer />
      <EndOnSessionEnd token={livekitToken} />
    </LiveKitRoom>
  );
};

export default PrecheckPlaygroundPage;
